<template>
  <div>
    <v-widget title="Danh sách sản phẩm">
      <template v-slot:widget-header-action>
        <v-btn
          v-if="order && order.ShopID"
          color="info"
          class="rounded-lg elevation-1 font-weight-bold mr-2"
          small
          @click="openShopOrders"
        >
          <v-icon left small>fa-store</v-icon> Đơn cùng shop
        </v-btn>
        <v-btn
          v-if="canEditProductValues && hasProductChanges"
          :loading="savingProducts"
          :disabled="savingProducts"
          color="success"
          class="rounded-lg elevation-1 font-weight-bold mr-2"
          small
          @click="saveProductChanges"
        >
          <v-icon left small>fa-save</v-icon> Lưu lại
        </v-btn>
        <v-btn
          @click="appendProductShow"
          v-if="(order.Status == statusEnum.cho_dat_coc && (orderEditRole & 4) && !orderReadonly) || adminRole"
          color="primary"
          class="rounded-lg elevation-1 font-weight-bold"
          small
        >
          <v-icon left small>fa-plus</v-icon> Thêm link
        </v-btn>
      </template>
      <template v-slot:widget-content>
        <v-data-table
          :headers="productHeader"
          :items="products"
          :mobile-breakpoint="100"
          class="elevation-0 rounded-lg overflow-hidden border"
        >
          <template v-slot:item="{ item, index }">
            <tr
              @click.exact="productSelectedIndex = index"
              @click.middle="productSelectedIndex = index"
              :class="productSelectedIndex === index ? 'product-highlight-row' : ''"
              :id="'r-' + item.ID"
            >
              <td>{{ index + 1 }}</td>
              <td>
                <a target="blank" :href="item.link_origin" class="font-weight-bold primary--text">{{ item.title_origin || item.ID }}</a>
              </td>
              <td>
                <img
                  class="image-product rounded-lg"
                  :src="proxyImage(item.image_origin && item.image_origin.includes('firebase') ? item.image_origin : decodeURIComponent(item.image_origin))"
                  alt
                />
              </td>
              <td>{{ item.property }}</td>
              <td class="text-end product-edit-cell">
                <div
                  v-if="canEditProductValues && productDrafts[item.ID]"
                  class="product-inline-editor"
                  :class="{ 'product-edit-changed': isProductFieldChanged(item, 'quantity') }"
                >
                  <h-currency-input
                    v-model="productDrafts[item.ID].quantity"
                    :decimal="0"
                    class="product-inline-input"
                  ></h-currency-input>
                </div>
                <span v-else>{{ item.quantity }}</span>
              </td>
              <td class="text-end font-weight-bold product-edit-cell">
                <div
                  v-if="canEditProductValues && productDrafts[item.ID]"
                  class="product-inline-editor product-price-editor"
                  :class="{ 'product-edit-changed': isProductFieldChanged(item, 'price_promotion') }"
                >
                  <span class="product-currency-symbol">¥</span>
                  <h-currency-input
                    v-model="productDrafts[item.ID].price_promotion"
                    :decimal="2"
                    class="product-inline-input"
                  ></h-currency-input>
                </div>
                <template v-else>{{ item.price_promotion | currency('¥', 2) }}<br></template>
                <span class="grey--text">{{ getProductValue(item, 'price_promotion') * order.ExchangeRate | currency }}<sup>₫</sup></span>
              </td>
              <td class="text-end font-weight-bold primary--text">
                {{ getProductValue(item, 'quantity') * getProductValue(item, 'price_promotion') | currency('¥', 2) }}
                <br>
                <span class="grey--text">{{ getProductValue(item, 'quantity') * getProductValue(item, 'price_promotion') * order.ExchangeRate | currency }}<sup>₫</sup></span>
              </td>
              <td class="text-center">
                <v-btn
                  text
                  icon
                  color="primary"
                  @click="editProduct(item)"
                  v-if="canEditProductValues"
                >
                  <v-icon small>fa-edit</v-icon>
                </v-btn>
                <v-btn
                  text
                  icon
                  color="error"
                  @click="removeProduct(item)"
                  v-if="(productEditable && (orderEditRole & 4) && !orderReadonly) || adminRole"
                >
                  <v-icon small>fa-trash-alt</v-icon>
                </v-btn>
              </td>
            </tr>
            <tr v-if="item.comment && item.comment !== ''">
              <td colspan="8" class="text-red px-4 py-2 grey lighten-5">
                <b>Ghi chú: </b>{{ item.comment }}
              </td>
            </tr>
          </template>
          <template v-slot:body.append>
            <tr class="grey lighten-4">
              <td colspan="4" class="font-weight-bold py-3">Tổng tiền hàng</td>
              <td class="text-end font-weight-bold py-3">{{ draftTotalProduct | currency }}</td>
              <td></td>
              <td class="text-end font-weight-bold py-3">
                {{ draftAmountCNY | currency('CNY', 2) }}
              </td>
              <td class="text-end font-weight-bold"><span class="grey--text">{{ draftAmountCNY * order.ExchangeRate | currency }}<sup>₫</sup></span></td>
            </tr>
            <tr v-if="(orderRole & 256) || complainRole || csRole || internalRole">
              <td colspan="4" class="font-weight-bold py-3">Phí ship nội địa</td>
              <td class="text-end font-weight-bold py-3"></td>
              <td></td>
              <td class="text-end font-weight-bold py-3">
                <h-currency-input
                  :disabled="saleRole || csRole || orderReadonly || !productEditable || (!(orderEditRole & 64) && !editableRole)"
                  class="fee-cn-footer"
                  v-model="localFeeShipCNY"
                  label="¥"
                  @press_enter="manualUpdateDetail"
                  @on_blur="manualUpdateDetail"
                ></h-currency-input>
              </td>
              <td class="text-end font-weight-bold"><span class="grey--text">{{ localFeeShipCNY * order.ExchangeRate | currency }}<sup>₫</sup></span></td>
            </tr>
            <tr class="grey lighten-4">
              <td colspan="4" class="font-weight-bold py-3">Tổng cộng</td>
              <td class="text-end font-weight-bold py-3"></td>
              <td></td>
              <td class="text-end font-weight-bold py-3">{{ draftAmountCNY + orderEditable.FeeShipCN | currency('CNY', 2) }}</td>
              <td class="text-end font-weight-bold"><span class="grey--text">{{ (draftAmountCNY + orderEditable.FeeShipCN) * order.ExchangeRate | currency }}<sup>₫</sup></span></td>
            </tr>
            <tr v-if="realAmountRole">
              <td colspan="4" class="font-weight-bold py-3">Tiền mua thật</td>
              <td class="text-end font-weight-bold py-3"></td>
              <td></td>
              <td class="text-end font-weight-bold py-3">
                <h-currency-input
                  :disabled="internalRole || orderReadonly || !productEditable || !(orderEditRole & 128) || (order.Status >= statusEnum.cho_mua_hang && purchaseRole)"
                  class="fee-cn-footer"
                  v-model="localTotalRealAmountCNY"
                  label="¥"
                  @press_enter="handleRealAmountUpdate"
                  @on_blur="handleRealAmountUpdate"
                ></h-currency-input>
              </td>
              <td class="text-end font-weight-bold"><span class="grey--text">{{ localTotalRealAmountCNY * order.ExchangeRate | currency }}<sup>₫</sup></span></td>
            </tr>
            <tr v-if="adminRole || purchaseRole">
              <td colspan="4" class="font-weight-bold py-3">Tiền hoa hồng</td>
              <td class="text-end font-weight-bold py-3"></td>
              <td></td>
              <td class="text-end font-weight-bold py-3">{{ commissionCNY | currency('CNY', 2) }}</td>
              <td class="text-end font-weight-bold"><span class="grey--text">{{ commissionVND | currency }}<sup>₫</sup></span></td>
            </tr>
          </template>
        </v-data-table>
      </template>
    </v-widget>

    <!-- Dialogs -->
    <v-dialog v-model="dialogProduct" max-width="500px">
      <v-card>
        <v-card-title>
          <span class="headline">Cập nhật sản phẩm</span>
        </v-card-title>
        <v-card-text>
          <v-container grid-list-md>
            <v-layout wrap>
              <v-flex xs12 sm12 md12>
                <v-checkbox v-model="editedPro.update_all" label="Cập nhật giá tất cả sản phẩm"></v-checkbox>
              </v-flex>
              <v-flex xs12 sm6 md4 v-if="!editedPro.update_all">
                <v-text-field type="number" v-model="editedPro.quantity" label="Số lượng"></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 md4>
                <v-text-field type="number" step=".1" v-model="editedPro.price_promotion" label="Đơn giá"></v-text-field>
              </v-flex>
              <v-flex xs12 sm12 md12 v-if="!editedPro.update_all">
                <v-textarea rows="3" v-model="editedPro.comment" label="Ghi chú" class="input-group--focused"></v-textarea>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" dark @click="closeProductDialog">Đóng</v-btn>
          <v-btn color="red darken-1" dark @click="saveProduct">Lưu lại</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogAppendProduct" max-width="1500px">
      <v-card class="append-product-dialog">
        <v-card-title class="append-product-dialog__header">
          <v-icon color="primary" class="mr-3">fa-link</v-icon>
          <div>
            <div class="title font-weight-bold">Thêm link sản phẩm</div>
            <div class="caption grey--text text--darken-1">Nhập thông tin sản phẩm cần bổ sung vào đơn hàng</div>
          </div>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-5">
          <div v-if="newProducts.length" class="mb-6">
            <div class="subtitle-2 font-weight-bold mb-3">
              Danh sách chờ thêm
              <v-chip color="primary" text-color="white" x-small class="ml-1">{{ newProducts.length }}</v-chip>
            </div>
            <v-card
              v-for="(item, index) in newProducts"
              :key="index"
              outlined
              class="append-product-item mb-3 pa-3"
            >
              <v-row align="center">
                <v-col cols="12" md="4">
                  <v-text-field v-model="item.TitleOrigin" label="Tên sản phẩm" dense hide-details></v-text-field>
                </v-col>
                <v-col cols="12" md="5">
                  <v-text-field v-model="item.Link" label="Link sản phẩm" dense hide-details></v-text-field>
                </v-col>
                <v-col cols="10" md="2" class="text-center">
                  <img
                    v-if="!hideImage && getImages(index)"
                    :src="getImages(index)"
                    class="append-product-image"
                    alt="Ảnh sản phẩm"
                  />
                  <div v-else class="append-product-image-placeholder">
                    <v-icon small color="grey">fa-image</v-icon>
                  </div>
                </v-col>
                <v-col cols="2" md="1" class="text-center">
                  <v-btn icon color="error" @click="newProducts.splice(index, 1)">
                    <v-icon small>fa-trash-alt</v-icon>
                  </v-btn>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field v-model="item.Property" label="Thuộc tính" dense hide-details></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <h-currency-input v-model="item.Price" label="Đơn giá (¥)" :decimal="1"></h-currency-input>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <h-currency-input v-model="item.Quantity" label="Số lượng" :decimal="0"></h-currency-input>
                </v-col>
              </v-row>
            </v-card>
          </div>

          <v-card outlined class="append-product-form pa-4">
            <div class="subtitle-2 font-weight-bold primary--text mb-3">Thông tin sản phẩm mới</div>
            <v-row align="center">
              <v-col cols="12" md="4">
                <v-text-field v-model="newProduct.TitleOrigin" label="Tên sản phẩm"></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="newProduct.Link" label="Link sản phẩm"></v-text-field>
              </v-col>
              <v-col cols="12" md="2" class="text-center">
                <div class="append-product-upload">
                  <img
                    v-if="!hideImage && newProduct.Image"
                    :src="newProduct.Image"
                    class="append-product-image"
                    alt="Ảnh sản phẩm"
                  />
                  <div v-else class="append-product-image-placeholder">
                    <v-icon color="grey">fa-image</v-icon>
                  </div>
                  <input type="file" ref="fileInput" class="d-none" @change="previewImage" accept="image/*" />
                  <v-btn text small color="primary" class="mt-1" @click="chooseImage">
                    <v-icon left small>fa-upload</v-icon>Tải ảnh
                  </v-btn>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="newProduct.Property" label="Thuộc tính"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <h-currency-input v-model="newProduct.Price" label="Đơn giá (¥)" :decimal="1"></h-currency-input>
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <h-currency-input v-model="newProduct.Quantity" label="Số lượng" :decimal="0"></h-currency-input>
              </v-col>
              <v-col cols="12" md="2" class="text-right">
                <v-btn color="primary" class="rounded-lg" block @click="appendProduct">
                  <v-icon left small>fa-plus</v-icon>Thêm vào danh sách
                </v-btn>
              </v-col>
            </v-row>
          </v-card>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text color="grey darken-1" @click="dialogAppendProduct = false">Đóng</v-btn>
          <v-btn color="primary" class="rounded-lg px-6" @click="createNewOrder">
            <v-icon left small>fa-save</v-icon>Lưu sản phẩm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <shop-orders-popup
      v-model="dialogShopOrders"
      :shop-id="selectedShopId"
      :shop-label="selectedShopLabel"
    ></shop-orders-popup>
  </div>
</template>

<script>
import VWidget from "@/components/VWidget";
import ShopOrdersPopup from "@/components/popups/ShopOrdersPopup";
import { getOrderShopLabel } from "@/commons/orderInternalMapper";
import { sendErrorNotice, sendSuccessNotice, makeid, proxyImage } from "@/commons/utils";
import configs from "@/configs";

export default {
  name: "TabProducts",
  components: {
    VWidget,
    ShopOrdersPopup
  },
  props: {
    order: {
      type: Object,
      required: true
    },
    orderEditable: {
      type: Object,
      required: true
    },
    products: {
      type: Array,
      required: true
    },
    productEditable: {
      type: Boolean,
      required: true
    },
    orderEditRole: {
      type: Number,
      required: true
    },
    orderReadonly: {
      type: Boolean,
      required: true
    },
    adminRole: {
      type: Boolean,
      required: true
    },
    orderRole: {
      type: Number,
      required: true
    },
    complainRole: {
      type: Boolean,
      required: true
    },
    csRole: {
      type: Boolean,
      required: true
    },
    internalRole: {
      type: Boolean,
      required: true
    },
    realAmountRole: {
      type: Boolean,
      required: true
    },
    editableRole: {
      type: Boolean,
      required: true
    },
    saleRole: {
      type: Boolean,
      required: true
    },
    totalProduct: {
      type: Number,
      required: true
    },
    feeShipCny: {
      type: [Number, String],
      required: true
    },
    totalRealAmountCny: {
      type: [Number, String],
      required: true
    },
    requireRealMoney: {
      type: Array,
      required: true
    },
    productHeader: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      statusEnum: configs.ORDER_STATUS_ENUM,
      proxyImage,
      productSelectedIndex: -1,
      dialogProduct: false,
      dialogAppendProduct: false,
      dialogShopOrders: false,
      selectedShopId: null,
      selectedShopLabel: "",
      hideImage: false,
      imageData: null,
      editedPro: {
        quantity: 0,
        price_promotion: 0,
        comment: "",
        update_all: false
      },
      newProduct: {
        TitleOrigin: "",
        Link: "",
        Image: "",
        Property: "",
        Price: 0,
        Quantity: 1
      },
      newProducts: [],
      productDrafts: {},
      savingProducts: false
    };
  },
  watch: {
    products: {
      immediate: true,
      handler(products) {
        const drafts = {};
        (products || []).forEach(item => {
          drafts[item.ID] = {
            quantity: Number(item.quantity || 0),
            price_promotion: Number(item.price_promotion || 0)
          };
        });
        this.productDrafts = drafts;
      }
    }
  },
  computed: {
    localFeeShipCNY: {
      get() {
        return this.feeShipCny;
      },
      set(val) {
        this.$emit("update:feeShipCny", val);
      }
    },
    localTotalRealAmountCNY: {
      get() {
        return this.totalRealAmountCny;
      },
      set(val) {
        this.$emit("update:totalRealAmountCny", val);
      }
    },
    purchaseRole() {
        return (this.$store.state.authentication.user.roletype & configs.ROLE_TYPE.Purchase) == configs.ROLE_TYPE.Purchase;
    },
    canEditProductValues() {
      const hasBasePermission = (this.productEditable && (this.orderEditRole & 4) && !this.orderReadonly) || this.adminRole;
      return hasBasePermission && (!this.saleRole || Number(this.order.Status) === 0);
    },
    hasProductChanges() {
      return (this.products || []).some(item =>
        this.isProductFieldChanged(item, 'quantity') ||
        this.isProductFieldChanged(item, 'price_promotion')
      );
    },
    draftTotalProduct() {
      return (this.products || []).reduce((total, item) => total + Number(this.getProductValue(item, 'quantity') || 0), 0);
    },
    draftAmountCNY() {
      return (this.products || []).reduce((total, item) => {
        const quantity = Number(this.getProductValue(item, 'quantity') || 0);
        const price = Number(this.getProductValue(item, 'price_promotion') || 0);
        return total + quantity * price;
      }, 0);
    },
    commissionCNY() {
      const realAmount = Number(this.localTotalRealAmountCNY || 0);
      if (realAmount <= 0) return 0;
      const totalCNY = Number(this.draftAmountCNY || 0) + Number(this.localFeeShipCNY || 0);
      return totalCNY - realAmount;
    },
    commissionVND() {
      return this.commissionCNY * Number(this.order && this.order.ExchangeRate ? this.order.ExchangeRate : 0);
    }
  },
  methods: {
    openShopOrders() {
      if (!this.order || !this.order.ShopID) {
        return;
      }
      this.selectedShopId = this.order.ShopID;
      this.selectedShopLabel = getOrderShopLabel(this.order);
      this.dialogShopOrders = true;
    },
    getProductValue(item, field) {
      const draft = this.productDrafts[item.ID];
      return draft ? draft[field] : item[field];
    },
    isProductFieldChanged(item, field) {
      const draft = this.productDrafts[item.ID];
      return !!draft && Number(draft[field]) !== Number(item[field]);
    },
    async saveProductChanges() {
      const changedProducts = (this.products || [])
        .filter(item => this.isProductFieldChanged(item, 'quantity') || this.isProductFieldChanged(item, 'price_promotion'))
        .map(item => ({
          ID: item.ID,
          OrderID: this.order.ID,
          quantity: Number(this.productDrafts[item.ID].quantity || 0),
          price_promotion: Number(this.productDrafts[item.ID].price_promotion || 0)
        }));

      if (!changedProducts.length || !this.canEditProductValues) return;

      this.savingProducts = true;
      try {
        await this.$store.dispatch('main_order/updateProducts', {
          OrderID: this.order.ID,
          Products: changedProducts
        });
      } catch (error) {
        // The Vuex action already shows the save error to the user.
      } finally {
        this.savingProducts = false;
      }
    },
    appendProductShow() {
      this.dialogAppendProduct = true;
    },
    editProduct(item) {
      this.editedPro = Object.assign({}, item);
      this.dialogProduct = true;
    },
    removeProduct(item) {
      this.$emit("remove-product", item);
    },
    closeProductDialog() {
      this.dialogProduct = false;
    },
    saveProduct() {
      this.editedPro.OrderID = this.order.ID;
      this.$store.dispatch("main_order/updateProduct", this.editedPro).then(() => {
        this.$emit("refresh");
        this.dialogProduct = false;
      });
    },
    appendProduct() {
      this.newProduct.OrderID = this.order.ID;
      this.newProduct.UID = this.order.UID;
      this.newProducts.push(this.newProduct);
      this.newProduct = {
        TitleOrigin: "",
        Link: "",
        Image: "",
        Property: "",
        Price: 0,
        Quantity: 1
      };
    },
    createNewOrder() {
      if (this.newProducts.length === 0) {
        sendErrorNotice("Hãy nhập danh sách sản phẩm!");
        return;
      }
      this.$store.dispatch("main_order/createNewLink", {
        data: { Products: this.newProducts },
        id: this.order.ID
      });
      this.newProducts = [];
      this.dialogAppendProduct = false;
      sendSuccessNotice("Thêm mới link sản phẩm thành công.");
    },
    chooseImage() {
      this.newProduct.Image = "";
      this.$refs.fileInput.click();
    },
    previewImage(event) {
      this.imageData = event.target.files[0];
      this.$refs.fileInput.value = null;
      this.onUpload();
    },
    onUpload() {
      if (!this.imageData) return;
      let formData = new FormData();
      formData.append("file", this.imageData);

      this.$http
        .post(`${configs.API || ""}/admin/api/upload/r2/`, formData, {
          headers: { Authorization: "Bearer " + this.$store.state.authentication.token }
        })
        .then(res => {
          if (res.data.status === 200 && res.data.data && res.data.data.url) {
            this.newProduct.Image = res.data.data.url;
            this.hideImage = true;
            this.$nextTick(() => (this.hideImage = false));
          } else {
            sendErrorNotice("Upload thất bại: " + (res.data.message || res.data.error || ""));
          }
        })
        .catch(err => {
          sendErrorNotice("Lỗi kết nối khi upload file.");
        });
    },
    getImages(index) {
      return this.newProducts[index].Image;
    },
    manualUpdateDetail() {
      this.$emit("manual-update");
    },
    handleRealAmountUpdate() {
      const realAmount = Number(this.localTotalRealAmountCNY || 0);
      // if (realAmount > 0 && !(this.order.IsProcessedCN === true || this.order.IsProcessedCN === 1)) {
      //   sendErrorNotice("Đơn hàng chưa xử lý CN, không thể cập nhật tiền mua thật.");
      //   this.localTotalRealAmountCNY = this.order.TotalRealAmountCNY || 0;
      //   return;
      // }
      this.manualUpdateDetail();
    }
  }
};
</script>

<style scoped>
.image-product {
  width: 64px;
}
.product-highlight-row {
  background-color: bisque;
}
.fee-cn-footer {
  width: 100%;
}
.product-edit-cell {
  min-width: 120px;
}
.product-inline-editor {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 24px;
  padding: 0 4px;
  cursor: text;
}
.product-inline-input {
  flex: 0 1 90px;
  min-width: 0;
}
.product-currency-symbol {
  margin-right: 4px;
}
.product-edit-cell ::v-deep .product-inline-input .v-input {
  margin: 0;
  padding: 0;
}
.product-edit-cell ::v-deep .product-inline-input .v-input__control,
.product-edit-cell ::v-deep .product-inline-input .v-input__slot {
  min-height: 24px;
}
.product-edit-cell ::v-deep .product-inline-input .v-input__slot {
  margin: 0;
}
.product-edit-cell ::v-deep .product-inline-input .v-input__slot::before,
.product-edit-cell ::v-deep .product-inline-input .v-input__slot::after,
.product-edit-cell ::v-deep .product-inline-input .v-text-field__details {
  display: none;
}
.product-edit-cell ::v-deep .product-inline-input input {
  height: 24px;
  padding: 0 !important;
  color: inherit;
  font-size: inherit;
  font-weight: inherit;
  line-height: 24px;
  text-align: right;
}
.product-inline-editor:not(.product-edit-changed):hover {
  border-bottom: 1px dashed #1976d2;
}
.product-edit-changed {
  background-color: #fff3cd;
  border-radius: 4px;
}
.append-product-dialog__header {
  min-height: 76px;
  padding: 16px 24px;
}
.append-product-form {
  background-color: #f8fbff;
  border-color: #bbdefb !important;
  border-radius: 10px;
}
.append-product-item {
  border-radius: 10px;
}
.append-product-image,
.append-product-image-placeholder {
  width: 64px;
  height: 64px;
  border-radius: 8px;
}
.append-product-image {
  object-fit: cover;
  border: 1px solid #e0e0e0;
}
.append-product-image-placeholder {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #f2f2f2;
  border: 1px dashed #bdbdbd;
}
.append-product-upload {
  display: flex;
  flex-direction: column;
  align-items: center;
}
</style>
